import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck, Loader2, Lock, X as CloseIcon } from "lucide-react";
import {
  isWhatsAppDisplayNameValid,
  type RegisterWhatsAppPhoneResponse,
  type WhatsAppConnectionResponse,
  type WhatsAppPhoneRegistrationState,
} from "@multi-chat/shared";
import {
  attachWhatsAppPhone,
  createWhatsAppPhone,
  getConnection,
  getWhatsAppPhoneRegistrationStatus,
  requestWhatsAppPhoneVerificationCode,
  verifyWhatsAppPhoneCode,
} from "../../../api/whatsapp-api.js";
import { isApiError } from "../../../api/client.js";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { cn } from "../../../lib/utils.js";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion.js";
import { TechnicalValue } from "../TechnicalValue.js";
import { AddPhoneCategoryField } from "./AddPhoneCategoryField.js";
import { AddPhoneConnectFooter } from "./AddPhoneConnectFooter.js";
import { AddPhoneCountryPhoneField } from "./AddPhoneCountryPhoneField.js";
import { AddPhoneProgress, addPhoneProgressIndex } from "./AddPhoneProgress.js";
import {
  VerificationCodeStep,
  VerificationCooldownNotice,
  VerificationMethodStep,
} from "./VerificationSteps.js";
import { mapAddPhoneError, isVerificationCooldownRateLimit } from "./add-phone-errors.js";
import { useVerificationCooldown } from "./use-verification-cooldown.js";
import { registerVerifiedWhatsAppPhone } from "./add-phone-registration.js";
import {
  addPhoneStepDirection,
  addPhoneStepMotion,
  type AddPhoneStepDirection,
} from "./add-phone-wizard-motion.js";
import {
  addPhoneWizardSessionKey,
  canAdvanceFromBusinessProfile,
  canAdvanceFromPhoneNumber,
  createInitialAddPhoneWizardState,
  isUnrecoverablePendingPhone,
  isVerificationCooldownActive,
  registrationRequestFromWizardState,
  shouldAutoStartPhoneRegistration,
  canReturnToCodeEntry,
  rejectedVerificationCodeReason,
  verificationCodeRejectionReason,
  wizardStateAfterCodeRejected,
  wizardStateAfterSilentStatus,
  wizardStateAfterVerificationRequestFailure,
  wizardStateAfterVerificationRequestSuccess,
  wizardStateForChangeMethod,
  wizardStateForReceivedCode,
  wizardStepFromRegistrationState,
  nextWizardStepAfterStatusPoll,
  type AddPhoneWizardEntry,
  type AddPhoneWizardState,
  type AddPhoneWizardStep,
} from "./add-phone-wizard-state.js";
import { ownershipOtpSubmitCode } from "./ownership-otp.js";

const fieldInputClass =
  "h-10 w-full min-w-0 rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink transition-colors focus:border-accent disabled:cursor-not-allowed disabled:opacity-70";

export interface AddPhoneWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  entry: AddPhoneWizardEntry;
  sessionKey: string;
  variant?: "dialog" | "plain";
  unreachableMessage: string;
  onCompleted: (connection: WhatsAppConnectionResponse) => void;
  onBack?: () => void;
  /** Phone is gone or owned elsewhere. The pending page should offer cancel only. */
  onResumeUnavailable?: () => void;
}

function applyRegistrationState(
  current: AddPhoneWizardState,
  status: {
    externalPhoneNumberId: string;
    displayPhoneNumber: string | null;
    verifiedName: string | null;
    nameStatus?: string | null;
    registrationState: WhatsAppPhoneRegistrationState;
  },
): AddPhoneWizardState {
  return {
    ...current,
    externalPhoneNumberId: status.externalPhoneNumberId,
    displayPhoneNumber: status.displayPhoneNumber ?? current.displayPhoneNumber,
    verifiedName: status.verifiedName ?? current.verifiedName,
    nameStatus: status.nameStatus ?? current.nameStatus,
    step: wizardStepFromRegistrationState(status.registrationState),
  };
}

export function AddPhoneWizard({
  open,
  onOpenChange,
  organizationId,
  entry,
  sessionKey,
  variant = "dialog",
  unreachableMessage,
  onCompleted,
  onBack,
  onResumeUnavailable,
}: AddPhoneWizardProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const isRtl = i18n.dir() === "rtl";
  const [state, setState] = useState<AddPhoneWizardState>(() =>
    createInitialAddPhoneWizardState(entry),
  );
  const [busy, setBusy] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [registrationAttemptFailed, setRegistrationAttemptFailed] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const registrationTokenRef = useRef(0);
  const registrationOwnerRef = useRef<number | null>(null);
  /** Session token of the verify_code call in flight; `busy` lags two clicks in one tick. */
  const verifyInFlightRef = useRef<number | null>(null);
  const [stepDirection, setStepDirection] = useState<AddPhoneStepDirection>(1);
  const previousStepRef = useRef<AddPhoneWizardStep>(state.step);
  const cooldownNoticeId = useId();
  const clearError = (): void => {
    setError(null);
    setErrorCode(null);
    setRegistrationAttemptFailed(false);
  };
  /**
   * User-initiated step change. `error` belongs to the step that produced it; persistent
   * verification state (cooldown, unconfirmed delivery, rejected codes) lives in `state`.
   */
  const navigateTo = (next: (current: AddPhoneWizardState) => AddPhoneWizardState): void => {
    clearError();
    setState(next);
  };
  const reportError = (caught: unknown): void => {
    setErrorCode(isApiError(caught) ? (caught.code ?? null) : null);
    setError(
      isVerificationCooldownRateLimit(caught)
        ? null
        : mapAddPhoneError(t, caught, unreachableMessage, i18n.language),
    );
  };

  const applyRegisterResult = (result: RegisterWhatsAppPhoneResponse): void => {
    if (result.outcome === "connected") {
      setRegistrationAttemptFailed(false);
      setState((current) => ({ ...current, step: "SUCCESS" }));
      onCompleted(result.connection);
      return;
    }
    if (result.registrationStatus.registrationState === "needs_registration") {
      setRegistrationAttemptFailed(true);
      setErrorCode(null);
      setError(t("whatsapp.addPhone.errors.stillPending"));
      setState((current) => ({
        ...applyRegistrationState(current, result.registrationStatus),
        step: "CONNECTING",
      }));
      return;
    }
    setRegistrationAttemptFailed(false);
    setError(null);
    setErrorCode(null);
    setState((current) => applyRegistrationState(current, result.registrationStatus));
  };

  const runRegistration = async (
    phoneNumberId: string,
    profile: ReturnType<typeof registrationRequestFromWizardState>,
    token: number,
  ): Promise<void> => {
    if (token !== registrationTokenRef.current) return;
    if (registrationOwnerRef.current !== null) return;
    registrationOwnerRef.current = token;
    setBusy(true);
    setRegistrationAttemptFailed(false);
    setError(null);
    setErrorCode(null);
    setState((current) => ({ ...current, step: "CONNECTING" }));
    try {
      const result = await registerVerifiedWhatsAppPhone(organizationId, phoneNumberId, profile);
      if (token !== registrationTokenRef.current) return;
      applyRegisterResult(result);
    } catch (caught) {
      if (token !== registrationTokenRef.current) return;
      if (isApiError(caught) && caught.code === "WHATSAPP_REGISTRATION_PENDING") {
        setRegistrationAttemptFailed(false);
        setErrorCode(caught.code);
        setError(mapAddPhoneError(t, caught, unreachableMessage, i18n.language));
        setState((current) => ({ ...current, step: "REGISTRATION_PENDING" }));
        return;
      }
      setRegistrationAttemptFailed(true);
      reportError(caught);
    } finally {
      if (registrationOwnerRef.current === token) {
        registrationOwnerRef.current = null;
      }
      if (token === registrationTokenRef.current) {
        setBusy(false);
      }
    }
  };
  const runRegistrationRef = useRef(runRegistration);
  runRegistrationRef.current = runRegistration;
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const reportErrorRef = useRef(reportError);
  reportErrorRef.current = reportError;
  const onResumeUnavailableRef = useRef(onResumeUnavailable);
  onResumeUnavailableRef.current = onResumeUnavailable;
  const wizardSessionKey = addPhoneWizardSessionKey({ open, sessionKey, organizationId });

  useEffect(() => {
    const token = ++registrationTokenRef.current;
    if (!open) {
      return;
    }
    const currentEntry = entryRef.current;
    const initial = createInitialAddPhoneWizardState(currentEntry);
    setState(initial);
    setError(null);
    setErrorCode(null);
    setRegistrationAttemptFailed(false);
    setBusy(false);
    setVerifyingCode(false);
    previousStepRef.current = initial.step;
    setStepDirection(1);
    if (currentEntry.kind !== "existing") {
      setHydrating(false);
      return;
    }
    const existingPhoneId = currentEntry.externalPhoneNumberId;
    let cancelled = false;
    setHydrating(true);
    void getWhatsAppPhoneRegistrationStatus(organizationId, existingPhoneId)
      .then(async (status) => {
        if (cancelled || token !== registrationTokenRef.current) return;
        setState((current) => wizardStateAfterSilentStatus(current, status));
        setHydrating(false);
        if (!shouldAutoStartPhoneRegistration(status.registrationState)) return;
        await runRegistrationRef.current(
          existingPhoneId,
          registrationRequestFromWizardState(initial),
          token,
        );
      })
      .catch((caught: unknown) => {
        if (cancelled || token !== registrationTokenRef.current) return;
        if (
          isApiError(caught) &&
          isUnrecoverablePendingPhone(caught.code) &&
          onResumeUnavailableRef.current
        ) {
          onResumeUnavailableRef.current();
          return;
        }
        reportErrorRef.current(caught);
      })
      .finally(() => {
        if (!cancelled && token === registrationTokenRef.current) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wizardSessionKey, organizationId]);

  useEffect(() => {
    if (previousStepRef.current === state.step) {
      return;
    }
    setStepDirection(addPhoneStepDirection(previousStepRef.current, state.step));
    previousStepRef.current = state.step;
  }, [state.step]);

  const handleOpenChange = (nextOpen: boolean): void => {
    onOpenChange(nextOpen);
  };

  const phoneId = state.externalPhoneNumberId;
  const verificationSubmitCode = ownershipOtpSubmitCode(state.verificationCode);
  const rejectedCodeReason = rejectedVerificationCodeReason(state, verificationSubmitCode);
  const canSubmitCode =
    verificationSubmitCode !== null && rejectedCodeReason === null && !busy && phoneId !== null;
  const displayNameTooShort =
    state.displayNameInput.length > 0 && !isWhatsAppDisplayNameValid(state.displayNameInput);
  const phoneAlreadyExists =
    state.step === "PHONE_NUMBER" && errorCode === "WHATSAPP_PHONE_ALREADY_EXISTS";
  const cooldown = useVerificationCooldown(state.codeRetryAfter);
  const cooldownActive = cooldown.active;
  const cooldownNotice =
    cooldownActive && state.codeRetryAfter ? (
      <VerificationCooldownNotice
        id={cooldownNoticeId}
        remainingMs={cooldown.remainingMs}
        retryAfter={state.codeRetryAfter}
        retryAfterSource={state.codeRetryAfterSource}
      />
    ) : null;
  const displayedError = error;
  const errorNotice = displayedError ? (
    <AppNotice severity="error" description={displayedError} testId="add-phone-error" />
  ) : null;
  const codeErrorReported = verificationCodeRejectionReason(errorCode) !== null;
  const codeInvalid = codeErrorReported || rejectedCodeReason !== null;
  const codeStepNotice =
    rejectedCodeReason !== null ? (
      <AppNotice
        severity="error"
        description={t(
          rejectedCodeReason === "WHATSAPP_VERIFICATION_CODE_EXPIRED"
            ? "whatsapp.addPhone.errors.expiredCode"
            : "whatsapp.addPhone.errors.invalidCode",
        )}
        testId="add-phone-error"
      />
    ) : (
      errorNotice
    );
  const verificationStep =
    state.step === "VERIFICATION_METHOD" || state.step === "VERIFICATION_CODE";

  const stepTitle = useMemo(() => {
    switch (state.step) {
      case "BUSINESS_PROFILE":
        return t("whatsapp.addPhone.steps.profileTitle");
      case "PHONE_NUMBER":
        return t("whatsapp.addPhone.steps.phoneTitle");
      case "VERIFICATION_METHOD":
        return t("whatsapp.addPhone.steps.methodTitle");
      case "VERIFICATION_CODE":
        return t("whatsapp.addPhone.steps.codeTitle");
      case "CONNECTING":
        return t("whatsapp.addPhone.steps.connectingTitle");
      case "READY_TO_ATTACH":
        return t("whatsapp.addPhone.steps.attachTitle");
      case "REGISTRATION_PENDING":
        return t("whatsapp.addPhone.steps.pendingTitle");
      case "SUCCESS":
        return t("whatsapp.addPhone.steps.successTitle");
      default: {
        const _exhaustive: never = state.step;
        return _exhaustive;
      }
    }
  }, [state.step, t]);

  const handleCreatePhone = async (): Promise<void> => {
    if (!canAdvanceFromPhoneNumber(state) || busy || state.vertical === "") return;
    setBusy(true);
    clearError();
    try {
      const created = await createWhatsAppPhone(organizationId, {
        countryCallingCode: state.countryCallingCode.replace(/\D/g, ""),
        phoneNumber: state.phoneNumber.replace(/\D/g, ""),
        verifiedName: state.displayNameInput.trim(),
        vertical: state.vertical,
        description: state.description.trim() || undefined,
      });
      setState((current) => ({
        ...applyRegistrationState(current, created),
        businessProfileUpdated: created.businessProfileUpdated,
      }));
    } catch (caught) {
      reportError(caught);
    } finally {
      setBusy(false);
    }
  };

  const handleRequestCode = async (): Promise<void> => {
    if (!phoneId || busy || isVerificationCooldownActive(state.codeRetryAfter, Date.now())) return;
    setBusy(true);
    clearError();
    try {
      const language = i18n.language.startsWith("he") ? "he_IL" : "en_US";
      const sent = await requestWhatsAppPhoneVerificationCode(organizationId, phoneId, {
        codeMethod: state.codeMethod,
        language,
      });
      setState((current) => wizardStateAfterVerificationRequestSuccess(current, sent));
    } catch (caught) {
      const retryAfter = isApiError(caught) ? caught.details?.retryAfter : undefined;
      const retryAfterSource = isApiError(caught)
        ? caught.details?.retryAfterSource
        : undefined;
      setState((current) =>
        wizardStateAfterVerificationRequestFailure(current, {
          ...(retryAfter ? { retryAfter } : {}),
          ...(retryAfterSource === "provider" || retryAfterSource === "application"
            ? { retryAfterSource }
            : {}),
          codeDeliveryUnconfirmed:
            isApiError(caught) && caught.details?.codeDeliveryUnconfirmed === "true",
        }),
      );
      reportError(caught);
    } finally {
      setBusy(false);
    }
  };

  const handleAlreadyReceivedCode = (): void => {
    navigateTo(wizardStateForReceivedCode);
  };

  const handleChangeMethod = (): void => {
    navigateTo(wizardStateForChangeMethod);
  };

  const finishConnected = async (): Promise<void> => {
    const connection = await getConnection(organizationId);
    setState((current) => ({ ...current, step: "SUCCESS" }));
    onCompleted(connection);
  };

  const handleVerifyCode = async (): Promise<void> => {
    if (
      verificationSubmitCode === null ||
      !phoneId ||
      busy ||
      rejectedCodeReason !== null ||
      verifyInFlightRef.current === registrationTokenRef.current
    ) {
      return;
    }
    const code = verificationSubmitCode;
    const token = registrationTokenRef.current;
    verifyInFlightRef.current = token;
    const profile = registrationRequestFromWizardState(state);
    setBusy(true);
    setVerifyingCode(true);
    clearError();
    try {
      const status = await verifyWhatsAppPhoneCode(organizationId, phoneId, {
        code,
      });
      if (token !== registrationTokenRef.current) return;
      if (status.registrationState === "already_connected") {
        await finishConnected();
        return;
      }
      if (status.registrationState === "ready_to_attach") {
        const connection = await attachWhatsAppPhone(organizationId, phoneId);
        setState((current) => ({ ...current, step: "SUCCESS" }));
        onCompleted(connection);
        return;
      }
      if (status.registrationState === "needs_registration") {
        await runRegistration(phoneId, profile, token);
        return;
      }
      setState((current) => ({
        ...applyRegistrationState(current, status),
        verificationCode: "",
      }));
    } catch (caught) {
      if (token !== registrationTokenRef.current) return;
      const reason = isApiError(caught) ? verificationCodeRejectionReason(caught.code) : null;
      if (reason !== null) {
        setState((current) => wizardStateAfterCodeRejected(current, { code, reason }));
      }
      reportError(caught);
    } finally {
      if (verifyInFlightRef.current === token) verifyInFlightRef.current = null;
      if (token === registrationTokenRef.current) {
        setVerifyingCode(false);
        if (registrationOwnerRef.current === null) {
          setBusy(false);
        }
      }
    }
  };

  const handleRegister = (): void => {
    if (!phoneId || busy || registrationOwnerRef.current !== null) return;
    void runRegistration(
      phoneId,
      registrationRequestFromWizardState(state),
      registrationTokenRef.current,
    );
  };

  const handleCheckRegistrationStatus = async (): Promise<void> => {
    if (!phoneId || busy) return;
    setBusy(true);
    clearError();
    try {
      const status = await getWhatsAppPhoneRegistrationStatus(organizationId, phoneId);
      if (status.registrationState === "ready_to_attach") {
        const connection = await attachWhatsAppPhone(organizationId, phoneId);
        setState((current) => ({ ...current, step: "SUCCESS" }));
        onCompleted(connection);
        return;
      }
      if (status.registrationState === "already_connected") {
        handleOpenChange(false);
        return;
      }
      setState((current) => ({
        ...applyRegistrationState(current, status),
        step: nextWizardStepAfterStatusPoll(current.step, status.registrationState),
      }));
      if (
        status.registrationState === "registration_processing" ||
        (state.step === "REGISTRATION_PENDING" &&
          status.registrationState === "needs_registration")
      ) {
        setErrorCode(null);
        setError(t("whatsapp.addPhone.errors.stillPending"));
      }
    } catch (caught) {
      reportError(caught);
    } finally {
      setBusy(false);
    }
  };

  const handleAttachPhone = async (): Promise<void> => {
    if (!phoneId || busy) return;
    setBusy(true);
    clearError();
    try {
      const connection = await attachWhatsAppPhone(organizationId, phoneId);
      setState((current) => ({ ...current, step: "SUCCESS" }));
      onCompleted(connection);
    } catch (caught) {
      reportError(caught);
    } finally {
      setBusy(false);
    }
  };

  const goBack = (): void => {
    if (state.step === "PHONE_NUMBER") {
      navigateTo((current) => ({ ...current, step: "BUSINESS_PROFILE" }));
      return;
    }
    if (onBack && variant === "plain") {
      onBack();
      return;
    }
    handleOpenChange(false);
  };

  const renderStep = (): React.JSX.Element | null => {
    switch (state.step) {
      case "BUSINESS_PROFILE":
        return (
          <div className="grid gap-4">
            <p className="m-0 text-[0.875rem] leading-relaxed text-ink-muted">
              {t("whatsapp.addPhone.steps.profileDescription")}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5 text-start">
                <span className="text-[0.8rem] font-medium text-ink">
                  {t("whatsapp.addPhone.fields.displayName")}
                </span>
                <input
                  className={fieldInputClass}
                  value={state.displayNameInput}
                  maxLength={128}
                  aria-invalid={displayNameTooShort}
                  aria-describedby={displayNameTooShort ? "add-phone-display-name-error" : undefined}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setState((current) => ({ ...current, displayNameInput: event.target.value }))
                  }
                  disabled={busy}
                />
                {displayNameTooShort ? (
                  <span id="add-phone-display-name-error" className="text-[0.75rem] text-destructive" role="alert">
                    {t("whatsapp.addPhone.errors.displayNameTooShort")}
                  </span>
                ) : null}
              </label>
              <AddPhoneCategoryField
                value={state.vertical}
                disabled={busy}
                onChange={(vertical) => setState((current) => ({ ...current, vertical }))}
              />
            </div>
            <label className="grid gap-1.5 text-start">
              <span className="text-[0.8rem] font-medium text-ink">
                {t("whatsapp.addPhone.fields.description")}
              </span>
              <textarea
                className="min-h-24 w-full resize-y rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink disabled:opacity-70"
                maxLength={512}
                value={state.description}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setState((current) => ({ ...current, description: event.target.value.slice(0, 512) }))
                }
                disabled={busy}
                placeholder={t("whatsapp.addPhone.fields.descriptionPlaceholder")}
              />
              <span className="text-end text-[0.75rem] tabular-nums text-ink-muted">
                {t("whatsapp.addPhone.fields.descriptionHelp", { count: state.description.length })}
              </span>
            </label>
          </div>
        );
      case "PHONE_NUMBER":
        return (
          <div className="grid gap-4">
            <p className="m-0 text-[0.875rem] leading-relaxed text-ink-muted">
              {t("whatsapp.addPhone.steps.phoneDescription")}
            </p>
            <div className="grid gap-1.5">
              <AddPhoneCountryPhoneField
                countryCallingCode={state.countryCallingCode}
                nationalNumber={state.phoneNumber}
                disabled={busy}
                onCountryCallingCodeChange={(digits) =>
                  setState((current) => ({ ...current, countryCallingCode: digits }))
                }
                onNationalNumberChange={(digits) =>
                  setState((current) => ({ ...current, phoneNumber: digits }))
                }
              />
              <p className="m-0 text-[0.75rem] text-ink-muted">{t("whatsapp.addPhone.steps.phoneExample")}</p>
              {phoneAlreadyExists && error ? (
                <p className="m-0 text-[0.875rem] text-error" role="alert">
                  {displayedError}
                </p>
              ) : null}
            </div>
          </div>
        );
      case "VERIFICATION_METHOD":
        return (
          <VerificationMethodStep
            displayPhoneNumber={state.displayPhoneNumber}
            codeMethod={state.codeMethod}
            onMethodChange={(codeMethod) => setState((current) => ({ ...current, codeMethod }))}
            methodsDisabled={busy || cooldownActive}
            deliveryUnconfirmed={state.codeDeliveryUnconfirmed}
            returnToCodeAvailable={canReturnToCodeEntry(state)}
            onAlreadyReceivedCode={handleAlreadyReceivedCode}
            alreadyReceivedDisabled={busy || !phoneId}
            cooldown={cooldownNotice}
            notice={errorNotice}
          />
        );
      case "VERIFICATION_CODE":
        return (
          <VerificationCodeStep
            codeMethod={state.codeMethod}
            codeSent={state.codeSent}
            deliveryUnconfirmed={state.codeDeliveryUnconfirmed}
            displayPhoneNumber={state.displayPhoneNumber}
            verificationCode={state.verificationCode}
            onCodeChange={(verificationCode) => {
              if (codeErrorReported) clearError();
              setState((current) => ({ ...current, verificationCode }));
            }}
            codeInvalid={codeInvalid}
            busy={verifyingCode}
            verifyDisabled={!canSubmitCode || hydrating}
            onVerify={() => void handleVerifyCode()}
            resendDisabled={busy || !phoneId || cooldownActive}
            onResend={() => void handleRequestCode()}
            changeMethodDisabled={busy || cooldownActive}
            onChangeMethod={handleChangeMethod}
            cooldown={cooldownNotice}
            cooldownId={cooldownNotice ? cooldownNoticeId : undefined}
            notice={codeStepNotice}
          />
        );
      case "CONNECTING":
        return (
          <div
            className="grid justify-items-center gap-3 py-2 text-center"
            data-testid="add-phone-connect-progress"
            aria-busy={busy}
          >
            {busy ? (
              <Loader2 className="animate-spin text-primary" size={28} aria-hidden="true" />
            ) : null}
            {busy ? (
              <p className="m-0 text-[0.95rem] font-medium text-ink">
                {t("whatsapp.addPhone.steps.connectingDescription")}
              </p>
            ) : null}
            {state.displayPhoneNumber ? (
              <TechnicalValue className="font-mono text-[0.875rem] tabular-nums">
                {state.displayPhoneNumber}
              </TechnicalValue>
            ) : null}
            {registrationAttemptFailed ? null : (
              <p className="m-0 max-w-sm text-[0.8rem] leading-relaxed text-ink-muted">
                {t("whatsapp.addPhone.steps.connectingExplanation")}
              </p>
            )}
          </div>
        );
      case "READY_TO_ATTACH":
        return (
          <div className="grid gap-3">
            <p className="m-0 text-[0.875rem] text-ink-muted">
              {t("whatsapp.addPhone.steps.attachDescription")}
            </p>
            {state.displayPhoneNumber ? (
              <TechnicalValue className="inline-block font-mono text-[0.875rem] tabular-nums">
                {state.displayPhoneNumber}
              </TechnicalValue>
            ) : null}
            {state.verifiedName ? (
              <p className="m-0 text-[0.875rem] text-ink-muted">{state.verifiedName}</p>
            ) : null}
          </div>
        );
      case "REGISTRATION_PENDING":
        return (
          <div className="grid gap-3">
            <p className="m-0 text-[0.875rem] text-ink-muted">
              {t("whatsapp.addPhone.steps.pendingDescription")}
            </p>
            {state.displayPhoneNumber ? (
              <TechnicalValue className="inline-block font-mono text-[0.875rem] tabular-nums">
                {state.displayPhoneNumber}
              </TechnicalValue>
            ) : null}
          </div>
        );
      case "SUCCESS":
        return (
          <div className="grid justify-items-center gap-3 py-2 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-success-muted text-success">
              <CircleCheck size={28} aria-hidden="true" />
            </span>
            {state.displayPhoneNumber ? (
              <TechnicalValue className="font-mono text-[0.875rem] tabular-nums">
                {state.displayPhoneNumber}
              </TechnicalValue>
            ) : null}
            {state.verifiedName ? (
              <p className="m-0 text-[0.875rem] text-ink">{state.verifiedName}</p>
            ) : null}
            <Badge variant="success">{t("whatsapp.addPhone.steps.connectedStatus")}</Badge>
            <p className="m-0 max-w-sm text-[0.8rem] leading-relaxed text-ink-muted">
              {t("whatsapp.addPhone.steps.successDescription")}
            </p>
          </div>
        );
      default: {
        const _exhaustive: never = state.step;
        return _exhaustive;
      }
    }
  };

  const primaryAction = (): { label: string; onClick: () => void; disabled: boolean } | null => {
    switch (state.step) {
      case "BUSINESS_PROFILE":
        return {
          label: t("whatsapp.addPhone.actions.continue"),
          onClick: () => navigateTo((current) => ({ ...current, step: "PHONE_NUMBER" })),
          disabled: !canAdvanceFromBusinessProfile(state) || busy,
        };
      case "PHONE_NUMBER":
        return {
          label: busy
            ? t("whatsapp.addPhone.actions.continuing")
            : t("whatsapp.addPhone.actions.continueToVerification"),
          onClick: () => void handleCreatePhone(),
          disabled: !canAdvanceFromPhoneNumber(state),
        };
      case "VERIFICATION_METHOD":
        return {
          label: busy
            ? t("whatsapp.addPhone.actions.sendingCode")
            : t("whatsapp.addPhone.actions.sendCode"),
          onClick: () => void handleRequestCode(),
          disabled: !phoneId || cooldownActive,
        };
      case "VERIFICATION_CODE":
        return null;
      case "CONNECTING":
        return null;
      case "READY_TO_ATTACH":
        return {
          label: busy ? t("whatsapp.addPhone.actions.attaching") : t("whatsapp.addPhone.actions.attach"),
          onClick: () => void handleAttachPhone(),
          disabled: !phoneId,
        };
      case "REGISTRATION_PENDING":
        return {
          label: busy
            ? t("whatsapp.addPhone.actions.checkingStatus")
            : t("whatsapp.addPhone.actions.checkStatus"),
          onClick: () => void handleCheckRegistrationStatus(),
          disabled: false,
        };
      case "SUCCESS":
        return {
          label: t("whatsapp.addPhone.actions.done"),
          onClick: () => handleOpenChange(false),
          disabled: false,
        };
      default: {
        const _exhaustive: never = state.step;
        return _exhaustive;
      }
    }
  };

  const action = primaryAction();
  const stepMotion = addPhoneStepMotion({
    isRtl,
    reduceMotion: Boolean(reduceMotion),
  });
  const showNewPhoneProgress = entry.kind === "new";
  const showFooterAction = action != null;
  const stepContent = hydrating ? (
    <p className="m-0 flex items-center gap-2 text-[0.875rem] text-ink-muted">
      <Loader2 className="animate-spin" size={16} aria-hidden="true" />
      {t("whatsapp.addPhone.loadingStatus")}
    </p>
  ) : (
    <AnimatePresence mode="wait" initial={false} custom={stepDirection}>
      <motion.div
        key={addPhoneProgressIndex(state.step)}
        custom={stepDirection}
        variants={stepMotion.variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={stepMotion.transition}
        className="grid gap-4 overflow-x-hidden"
      >
        {renderStep()}
      </motion.div>
    </AnimatePresence>
  );
  const stepError =
    errorNotice && !phoneAlreadyExists && (!verificationStep || hydrating) ? errorNotice : null;
  const connectRetry =
    state.step === "CONNECTING" ? (
      <AddPhoneConnectFooter
        busy={busy || hydrating}
        registrationAttemptFailed={registrationAttemptFailed}
        errorCode={errorCode}
        disabled={!phoneId || hydrating}
        onRetry={handleRegister}
      />
    ) : null;
  const primaryButton =
    showFooterAction && action ? (
      <Button
        variant="default"
        className={variant === "dialog" ? "w-full" : undefined}
        onClick={action.onClick}
        disabled={action.disabled || busy || hydrating}
        data-testid="add-phone-primary-action"
      >
        {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
        {action.label}
      </Button>
    ) : null;

  if (variant === "plain") {
    // OTP entry already offers Change Method, which honours the resend cooldown.
    const showPlainFooterBack = state.step !== "SUCCESS" && state.step !== "VERIFICATION_CODE";
    return (
      <div className="grid content-start gap-4">
        {showNewPhoneProgress ? <AddPhoneProgress step={state.step} /> : null}
        <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
          <div className="grid gap-4 px-6 py-6 sm:px-8">
            <div
              className={cn(
                "grid gap-1",
                (state.step === "CONNECTING" || state.step === "SUCCESS") && "justify-items-center text-center",
              )}
            >
              <h2 className="m-0 text-base font-semibold text-ink">{stepTitle}</h2>
            </div>
            {stepContent}
            {stepError}
          </div>
          {showPlainFooterBack || connectRetry || primaryButton ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-muted/40 px-6 py-4 sm:px-8">
              {showPlainFooterBack ? (
                <Button variant="outline" onClick={goBack} disabled={busy || hydrating}>
                  {t("whatsapp.phoneManagement.back")}
                </Button>
              ) : null}
              {connectRetry}
              {primaryButton}
            </div>
          ) : null}
        </section>
      </div>
    );
  }

  const verificationProgressStep = state.step === "VERIFICATION_CODE" ? 2 : 1;
  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="w-[min(92vw,28rem)] gap-0 p-0">
        <Button
          type="button"
          variant="ghost"
          className="absolute end-3 top-3 size-8 p-0 text-ink-muted"
          onClick={() => handleOpenChange(false)}
          disabled={busy}
          aria-label={t("whatsapp.phoneManagement.close")}
        >
          <CloseIcon className="size-4" aria-hidden="true" />
        </Button>
        <header className="grid justify-items-center gap-2 px-6 pt-7 pb-4 text-center">
          <ChannelMark platformType="WHATSAPP" size="list" />
          <p className="m-0 text-[0.7rem] font-semibold tracking-[0.06em] text-success uppercase">
            {t("whatsapp.addPhone.verification.eyebrow")}
          </p>
          <AlertDialogTitle className="text-[1.2rem]">{stepTitle}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">
            {t("whatsapp.addPhone.dialogDescription")}
          </AlertDialogDescription>
          {verificationStep ? (
            <div className="mt-1 flex items-center gap-1.5" data-testid="add-phone-verification-progress">
              <span className="sr-only">
                {t("whatsapp.addPhone.verification.progressLabel", {
                  step: verificationProgressStep,
                })}
              </span>
              <span className="h-1 w-10 rounded-full bg-whatsapp" aria-hidden="true" />
              <span
                className={cn(
                  "h-1 w-10 rounded-full transition-colors duration-200",
                  verificationProgressStep === 2 ? "bg-whatsapp" : "bg-line",
                )}
                aria-hidden="true"
              />
            </div>
          ) : null}
        </header>
        <div className="grid gap-4 px-6 pb-6">
          {stepContent}
          {stepError}
          {connectRetry || primaryButton ? (
            <div className="grid gap-2">
              {connectRetry}
              {primaryButton}
            </div>
          ) : null}
        </div>
        <footer className="flex items-center justify-center gap-1.5 border-t border-line px-6 py-3 text-[0.75rem] text-ink-muted">
          <Lock className="size-3.5 shrink-0" aria-hidden="true" />
          {t("whatsapp.addPhone.verification.secureFooter")}
        </footer>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export type { AddPhoneWizardStep };
